import * as React from 'react';
import { Page } from 'components/Page';
import fonts from 'styles/typography.module.css';
import { DateRangeControls } from 'pages/PerDiemArchive/components/controls';
import {
  ArrowPathIcon,
  ArrowRightIcon,
  BanknotesIcon,
  ChartPieIcon,
} from '@heroicons/react/20/solid';
import { Button } from '@mui/material';
import { usePerDiemParams } from 'pages/PerDiemArchive/params';
import { Filter, getEmptyReturnAvailable } from 'services/clients/perDiem';
import { useQuery } from '@tanstack/react-query';
import { currencyFormatterNoCents, percentageFormatterNoDecimal } from 'utils/container-transforms';
import { Loader } from 'components/Loaders/loader';
import moment from 'moment';
import { REGION_MAP } from 'services/clients/regions';
import { FeatureFlag, useFeatureFlag } from 'utils/permissions/feature-flags';
import { useNavigate } from 'react-router-dom';
import { Routes } from 'constants/pages';
import { StatCard } from './StatCard';

export const PerDiemFighterCalculator: React.FC = () => {
  const { searchParams, setSearchParams } = usePerDiemParams(false);
  const navigate = useNavigate();

  // Construct our filter once all the values are provided
  const filter: Filter | undefined = React.useMemo(() => {
    const { shippingLine, container, startDate, endDate } = searchParams;
    if (!shippingLine || !container || !startDate || !endDate) {
      return undefined;
    }
    return {
      shippingLine,
      containerType: container,
      startDate: searchParams.startDate.format('YYYY-MM-DD'),
      endDate: searchParams.endDate.format('YYYY-MM-DD'),
      pageSize: searchParams.pageSize,
      page: searchParams.page,
      portCollection: 'USLA',
    };
  }, [searchParams]);

  const [showReport, setShowReport] = React.useState(false);

  const results = useQuery({
    queryKey: ['disputeDemo', filter],
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: ({ signal }) => getEmptyReturnAvailable(filter!, signal),
    enabled: !!(filter && showReport),
    keepPreviousData: true,
  });

  const clearSearchParams = React.useCallback(() => {
    setSearchParams({
      shippingLine: undefined,
      container: undefined,
      startDate: undefined,
      endDate: undefined,
      pageSize: 10,
      page: 1,
    });
    setShowReport(false);
  }, [setSearchParams]);

  const minStartDate = React.useMemo(() => {
    return moment(REGION_MAP.USLA.smartPerDiemMinDate).startOf('day');
  }, []);

  const showPage = useFeatureFlag(FeatureFlag.DisputeDemo);
  if (!showPage) {
    return (
      <Page>
        <h1>This page is unavailable.</h1>
      </Page>
    );
  }

  return (
    <Page className={`flex ${fonts.font}`}>
      <div className="flex flex-col grow">
        <div className="px-8 pt-28 pb-8 space-y-32 flex-grow">
          <div className={`flex flex-col space-y-3 ${showReport && results.data && 'text-center'}`}>
            <h1 className="font-bold text-4xl">Per Diem Fighter Calculator</h1>
            <h2 className="font-medium text-text-accent-3 text-xl">
              {showReport && results.data ? (
                <span>
                  Between {moment(filter?.startDate).format('MMM D, YYYY')} -{' '}
                  {moment(filter?.endDate).format('MMM D, YYYY')}
                </span>
              ) : (
                <span>
                  Browse historical empty receiving & appointment screenshots to Fight Per Diem
                </span>
              )}
            </h2>
          </div>
          {filter && showReport && results.isLoading && <Loader />}
          {showReport && results.isError && (
            <div className="flex flex-col space-y-3">
              <h3 className="font-medium text-text-accent-3 text-xl">
                Something went wrong. Please try again.
              </h3>
            </div>
          )}
          {results.data && showReport && (
            <div className="flex flex-col space-y-3">
              <div className="grid grid-cols-2 gap-6">
                <StatCard
                  icon={<BanknotesIcon className="w-[72px] h-[72px] text-blue-800" />}
                  title="Estimated savings"
                  value={currencyFormatterNoCents.format(
                    (results.data?.estimatedSavings || 0) / 100
                  )}
                />
                <StatCard
                  icon={<ChartPieIcon className="w-[72px] h-[72px] text-blue-800" />}
                  title="Dispute opportunity"
                  value={percentageFormatterNoDecimal.format(results.data?.opportunityToFight || 0)}
                />
              </div>
            </div>
          )}
          {(!filter || !showReport) && (
            <DateRangeControls
              minStartDate={minStartDate}
              withDefaults={false}
              containersToShow={["20'", "40'", '40HC', '45']}
            />
          )}
        </div>
        <div className="w-full px-10 space-x-4 py-4 bg-icon-interactive-accent-1 border-t flex justify-end">
          <Button
            className="!bg-orange-900 hover:!bg-orange-700 text-white py-2"
            onClick={() => navigate(Routes.SignUp)}
          >
            <h3 className="font-medium">Sign Up with BlueCargo</h3>
          </Button>
          {showReport ? (
            <Button
              disabled={!filter}
              onClick={clearSearchParams}
              variant="contained"
              color="primary"
            >
              <div className="flex items-center space-x-2">
                <h3 className="font-medium">Start new report</h3>
                <ArrowPathIcon className="w-6 h-6" />
              </div>
            </Button>
          ) : (
            <Button
              disabled={!filter}
              onClick={() => setShowReport(true)}
              className="py-2"
              variant="contained"
              color="primary"
            >
              <div className="flex items-center space-x-2">
                <h3 className="font-medium">Run report</h3>
                <ArrowRightIcon className="w-6 h-6" />
              </div>
            </Button>
          )}
        </div>
      </div>
    </Page>
  );
};
